import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SecuritySettings from './components/SecuritySettings';
import DocumentRetention from './components/DocumentRetention';
import InterfaceCustomization from './components/InterfaceCustomization';
import AuditConfiguration from './components/AuditConfiguration';
import IntegrationSettings from './components/IntegrationSettings';

const SystemSettings = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('security');

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const tabs = [
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      description: 'Password policies, authentication, and session management'
    },
    {
      id: 'retention',
      label: 'Document Retention',
      icon: 'Archive',
      description: 'Retention policies, storage optimization, and cleanup schedules'
    },
    {
      id: 'interface',
      label: 'Interface',
      icon: 'Palette',
      description: 'Branding, layout, localization, and notification preferences'
    },
    {
      id: 'audit',
      label: 'Audit & Compliance',
      icon: 'FileText',
      description: 'Audit logging, compliance settings, and reporting configuration'
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: 'Plug',
      description: 'API settings, webhooks, and external service connections'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'security':
        return <SecuritySettings />;
      case 'retention':
        return <DocumentRetention />;
      case 'interface':
        return <InterfaceCustomization />;
      case 'audit':
        return <AuditConfiguration />;
      case 'integrations':
        return <IntegrationSettings />;
      default:
        return <SecuritySettings />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={handleMenuToggle}
        isMenuOpen={sidebarOpen}
      />
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground">System Settings</h1>
                <p className="text-muted-foreground">
                  Configure platform settings, security policies, and system integrations
                </p>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/dashboard'}
                className="p-0 h-auto font-normal"
              >
                Dashboard
              </Button>
              <Icon name="ChevronRight" size={16} />
              <span className="text-foreground">System Settings</span>
            </nav>
          </div>

          {/* Settings Navigation Tabs */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="border-b border-border">
              <nav className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-3 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <div className="text-left">
                      <div>{tab?.label}</div>
                      <div className="text-xs text-muted-foreground hidden sm:block">
                        {tab?.description}
                      </div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-card border border-border rounded-lg">
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>

          {/* System Information Footer */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Server" size={20} className="text-primary" />
                <h3 className="font-medium text-foreground">System Status</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Version:</span>
                  <span className="text-foreground">v2.1.4</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Uptime:</span>
                  <span className="text-foreground">15 days, 8 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="text-success">Healthy</span>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Database" size={20} className="text-accent" />
                <h3 className="font-medium text-foreground">Storage Usage</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Documents:</span>
                  <span className="text-foreground">38.2 GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Audit Logs:</span>
                  <span className="text-foreground">2.1 GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Used:</span>
                  <span className="text-foreground">40.3 GB / 100 GB</span>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Users" size={20} className="text-success" />
                <h3 className="font-medium text-foreground">User Activity</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Active Users:</span>
                  <span className="text-foreground">127</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Users:</span>
                  <span className="text-foreground">245</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sessions Today:</span>
                  <span className="text-foreground">1,847</span>
                </div>
              </div>
            </div>
          </div>

          {/* Last Updated Info */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>
              System settings last updated on November 16, 2025 at 8:38 PM EST
            </p>
            <p className="mt-1">
              Changes to critical settings may require system restart
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SystemSettings;